import {
  AiIcon,
  CloseIcon,
  FavoriteIcon,
  HelpCenterIcon,
  HelpIcon,
  InfoIcon,
  InformationFillDuotoneIcon,
  LightModeIcon,
  NewIcon,
} from '@blocksuite/icons/rc';
import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  hideProTipSlot,
  type ProTipConfig,
  type ProTipIconName,
  type ProTipPlacement,
  showProTipSlot,
} from '../../components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import * as styles from './pro-tip.css';

const DEFAULT_AUTO_HIDE_DELAY = 10000;

const iconMap: Record<
  Exclude<ProTipIconName, 'none'>,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  lightMode: LightModeIcon,
  info: InfoIcon,
  informationFillDuotone: InformationFillDuotoneIcon,
  help: HelpIcon,
  helpCenter: HelpCenterIcon,
  ai: AiIcon,
  new: NewIcon,
  favorite: FavoriteIcon,
};

const getIcon = (iconName: ProTipIconName | undefined) => {
  if (!iconName || iconName === 'none') return null;
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent /> : null;
};

const mapPlacement = (placement: ProTipPlacement): Placement => {
  return placement as Placement;
};

interface ActiveProTip extends ProTipConfig {
  targetElement: HTMLElement;
}

interface ProTipContextType {
  showProTip: (config: ProTipConfig) => void;
  hideProTip: (id: string) => void;
  hideAllProTips: () => void;
}

const ProTipContext = createContext<ProTipContextType | null>(null);

export const useProTip = () => {
  const context = useContext(ProTipContext);
  if (!context) {
    throw new Error('useProTip must be used within ProTipProvider');
  }
  return context;
};

interface ProTipPopupProps {
  config: ActiveProTip;
  onClose: () => void;
}

const ProTipPopup = ({ config, onClose }: ProTipPopupProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const timerRef = useRef<number>(undefined);

  const { refs, floatingStyles } = useFloating({
    placement: mapPlacement(config.placement || 'bottom'),
    elements: {
      reference: config.targetElement,
    },
    middleware: [
      offset(config.offset ?? 16),
      flip({ fallbackPlacements: ['top', 'bottom', 'left', 'right'] }),
      shift({ padding: 16, crossAxis: true }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 150);
  }, [onClose, isClosing]);

  useEffect(() => {
    const delay = config.autoHideDelay ?? DEFAULT_AUTO_HIDE_DELAY;
    if (delay > 0) {
      timerRef.current = window.setTimeout(() => {
        handleClose();
      }, delay);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [config.autoHideDelay, handleClose]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const floatingEl = refs.floating.current;
      if (floatingEl && !floatingEl.contains(target)) {
        handleClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleClose, refs.floating]);

  const icon = getIcon(config.icon);
  const showClose = config.showCloseButton !== false;

  return createPortal(
    <div
      ref={refs.setFloating}
      className={styles.proTipContainer}
      style={floatingStyles}
    >
      <div className={styles.proTipCard} data-closing={isClosing}>
        {showClose && (
          <button className={styles.proTipCloseButton} onClick={handleClose}>
            <CloseIcon />
          </button>
        )}
        <div className={styles.proTipHeader}>
          {icon && <div className={styles.proTipIconWrapper}>{icon}</div>}
          {config.title && (
            <h4 className={styles.proTipTitle}>{config.title}</h4>
          )}
        </div>
        <p
          className={`${styles.proTipContent} ${!icon ? styles.proTipContentNoIcon : ''}`}
          dangerouslySetInnerHTML={{ __html: config.content }}
        />
      </div>
    </div>,
    document.body
  );
};

interface ProTipProviderProps {
  children: ReactNode;
}

export const ProTipProvider = ({ children }: ProTipProviderProps) => {
  const [activeProTip, setActiveProTip] = useState<ActiveProTip | null>(null);
  const shownTipsRef = useRef<Set<string>>(new Set());

  const showProTip = useCallback((config: ProTipConfig) => {
    if (shownTipsRef.current.has(config.id)) {
      return;
    }

    if (activeProTip) {
      return;
    }

    let targetElement: HTMLElement | null = null;

    if (config.targetElement) {
      targetElement = config.targetElement;
    } else if (config.targetSelector) {
      targetElement = document.querySelector(config.targetSelector);
    }

    if (!targetElement) {
      return;
    }

    shownTipsRef.current.add(config.id);
    setActiveProTip({ ...config, targetElement });
  }, [activeProTip]);

  const hideProTip = useCallback((id: string) => {
    setActiveProTip(prev => {
      if (prev?.id === id) {
        return null;
      }
      return prev;
    });
  }, []);

  const hideAllProTips = useCallback(() => {
    setActiveProTip(null);
  }, []);

  useEffect(() => {
    const showDisposable = showProTipSlot.on(config => {
      showProTip(config);
    });

    const hideDisposable = hideProTipSlot.on(id => {
      hideProTip(id);
    });

    return () => {
      showDisposable.dispose();
      hideDisposable.dispose();
    };
  }, [showProTip, hideProTip]);

  return (
    <ProTipContext value={{ showProTip, hideProTip, hideAllProTips }}>
      {children}
      {activeProTip && (
        <ProTipPopup
          key={activeProTip.id}
          config={activeProTip}
          onClose={() => hideProTip(activeProTip.id)}
        />
      )}
    </ProTipContext>
  );
};
